/**
 * Thin client for the Eduspire payment API.
 *
 * In development `VITE_API_BASE_URL` is empty and Vite proxies `/api` to the
 * Express server on :3001. In production it points at the deployed API origin.
 */

const BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');

const url = (path) => `${BASE}${path}`;

/** Wraps a non-2xx response so callers get a message instead of a raw body. */
class ApiError extends Error {
  constructor(message, status, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

async function parse(response) {
  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    // A proxy or hosting error page — surface something readable.
    throw new ApiError('The payment server returned an unexpected response.', response.status, text.slice(0, 200));
  }

  if (!response.ok || data.success === false) {
    if (response.status === 502) {
      throw new ApiError('The API server is offline. Please start it by running "npm run dev:all" in terminal.', 502);
    }
    throw new ApiError(data.error || `Request failed (${response.status})`, response.status, data.details);
  }
  return data;
}

export async function postJson(path, body, { timeoutMs = 20000, headers = {} } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    return await parse(response);
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new ApiError('The payment server took too long to respond.', 408);
    }
    if (error instanceof ApiError) throw error;
    throw new ApiError(
      'Could not reach the payment server. Check your connection and try again.',
      0
    );
  } finally {
    clearTimeout(timer);
  }
}

export async function getJson(path, { timeoutMs = 15000 } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url(path), { signal: controller.signal });
    return await parse(response);
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new ApiError('Status check timed out.', 408);
    }
    if (error instanceof ApiError) throw error;
    throw new ApiError('Could not reach the payment server.', 0);
  } finally {
    clearTimeout(timer);
  }
}

/* ── Payment endpoints ───────────────────────────────────────────────────
   Named for what they do rather than for who processes them. The gateway has
   already been swapped once; when it happens again only the server changes. */

export const createPaymentOrder = (payload) => postJson('/api/payments/create-order', payload);

export const fetchPaymentStatus = (orderId) =>
  getJson(`/api/payments/status/${encodeURIComponent(orderId)}`);

/* ── Admin sign-in ───────────────────────────────────────────────────────
   The code is generated, emailed and verified on the server: SMTP cannot be
   spoken from a browser, and a code the browser generated would be a code the
   browser could read. `verifyLoginOtp` also returns the Firebase custom token
   the console exchanges for a real session. */

export const requestLoginOtp = (email) => postJson('/api/auth/send-otp', { email });

export const verifyLoginOtp = (email, otp) => postJson('/api/auth/verify-otp', { email, otp });

/* ── Admin actions ───────────────────────────────────────────────────────
   Authorised by the Firebase ID token of the session the console is already
   holding. The API verifies that signature against Google's keys and checks
   the address against the same allowlist firestore.rules uses — an endpoint
   that emails a customer their invoice cannot be open to whoever finds it. */

async function adminAuthHeader() {
  const { loadFirebase } = await import('../firebase');
  const fb = await loadFirebase();
  const user = fb.auth.currentUser;

  if (!user) {
    throw new ApiError('Your admin session has expired — sign in again.', 401);
  }
  return { Authorization: `Bearer ${await user.getIdToken()}` };
}

/**
 * Send a paid customer their invoice and WhatsApp group link again.
 *
 * @param {string} orderId
 * @param {string} [email] A corrected address; omit to use the one on record.
 */
export const resendInvoiceEmail = async (orderId, email = '') =>
  postJson(
    '/api/admin/resend-invoice',
    { orderId, email },
    // Rendering a PDF and handing it to Gmail is slower than a JSON round trip.
    { headers: await adminAuthHeader(), timeoutMs: 45000 }
  );

export { ApiError };
